import { Button, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { useState } from "react";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import {
  combineValidators,
  maxCharactersValidator,
  regexValidator,
  requiredValidator,
  uniqueValidator,
  validResult,
} from "@entrilia/shared/utilities/validators";
import ObjectLayoutContext from "./ObjectLayoutContext";
import { addGroupAction, renameGroupAction } from "./objectLayoutState";

interface Props {
  onClose: () => void;
  editedGroupName?: string;
}

const EditGroupDialog = ({ onClose, editedGroupName }: Props) => {
  const { layoutState, setLayoutState } = useDefinedContext(ObjectLayoutContext);

  const [groupName, setGroupName] = useState(editedGroupName ?? "");
  const [validationResult, setValidationResult] = useState<ValidationResult>(validResult);

  const existingGroupNames = layoutState.fieldGroups.map((g) => g.name);

  const maxLength = 50;

  const nameValidator = combineValidators(
    requiredValidator,
    uniqueValidator("A group with this name already exists", existingGroupNames),
    regexValidator(/^[a-zA-Z0-9\s]+$/, "Name can only contain letters, numbers and spaces"),
    maxCharactersValidator(maxLength)
  );

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGroupName(value);
    setValidationResult(nameValidator(value));
  };

  const handleApply = () => {
    onClose();
    setLayoutState(editedGroupName ? renameGroupAction(editedGroupName, groupName) : addGroupAction(groupName));
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>{editedGroupName ? "Edit Group" : "Add Group"}</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <TextField
          fullWidth
          variant="outlined"
          label="Name"
          value={groupName}
          onChange={handleNameChange}
          error={!validationResult.isValid}
          helperText={validationResult.error}
          slotProps={{
            input: {
              endAdornment: (
                <Typography className="counter-adornment" color="textSecondary" sx={{ visibility: "hidden" }}>
                  {Math.max(0, maxLength - groupName.length)}
                </Typography>
              ),
            },
          }}
          sx={{ ".MuiInputBase-root": { "&.Mui-focused": { ".counter-adornment": { visibility: "visible" } } } }}
        />
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleApply}
          disabled={!validationResult.isValid || !groupName}
        >
          {editedGroupName ? "Apply" : "Add"}
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default EditGroupDialog;
